import { NewMaintenanceRequest } from '~/entities/MaintenanceRequestTypes';
import { AppState } from '~/framework/appState';
import { CompanyIdProvider } from '~/gateways/network/companyIdProvider';
import { AutoPayment, AutoPaymentRequest } from '~/types/AutoPayment';
import { Chat } from '~/types/Chat';
import { CompanyInfo } from '~/types/CompanyInfo';
import { Insurance } from '~/types/Insurance';
import { Lease } from '~/types/Lease';
import { LeaseRentersInsuranceEmbedDto } from '~/types/LeaseRentersInsuranceEmbedDto';
import { AuthCompanyOptionDto } from '~/types/MagicLink';
import { MaintenanceRequest, PaginatedMaintenanceRequests } from '~/types/MaintenanceRequest';
import { MoveOutRequest } from '~/types/MoveOutRequest';
import { PaymentWithFees } from '~/types/PaymentFees';
import { PaymentType } from '~/types/PaymentInfo';
import { SignablePdf } from '~/types/SignablePdf';
import { Tenant } from '~/types/Tenant';
import { BankAccount } from '~/types/TenantBankAccount';
import { LeaseTransaction } from '~/types/Transaction';
import { Annotation } from '~/types/annotation';

export interface PlaidInfo {
  linkToken?: string | null;
  publicToken?: string | null;
}

export interface StripeInfo {
  clientSecret?: string;
  sessionId?: string;
  isCheckoutFulfilled?: boolean;
}

export interface MaintenanceRequestInfo {
  maintenanceRequests?: PaginatedMaintenanceRequests;
  newRequest?: NewMaintenanceRequest;
  selectedMaintenanceRequest?: MaintenanceRequest;
}

export interface RentalApplicationCredentials {
  id: string;
  password: string;
}

export interface ChatSection {
  chats?: Chat[];
  selectedChat?: Chat;
}

export interface PaymentInfo {
  bankAccounts?: BankAccount[];
  plaid?: PlaidInfo;
  stripe: StripeInfo;
  selectedTransaction?: LeaseTransaction;
  autoPaymentDetail?: AutoPayment;
  autoPayments?: Map<string, AutoPayment[]>;
  selectedPaymentType?: PaymentType;
  selectedPaymentAmount?: number;
  selectedAutoPayment: SelectedAutoPayment;
  paymentWithFees?: PaymentWithFees;
  validationError?: Error;
}

export enum PaymentErrorCode {
  ACCOUNT_ERROR,
  PAYMENT_TYPE_ERROR,
  PAYMENT_FREQUENCY_ERROR,
  MAXIMUM_AMOUNT_ERROR,
  FIXED_AMOUNT_ERROR,
  START_DATE_ERROR,
  END_DATE_ERROR,
}

export interface SelectedAutoPayment {
  autoPayment?: AutoPaymentRequest;
  errors: Map<PaymentErrorCode, string>;
}

export interface SignerState {
  document: SignablePdf;
  signedAnnotations: Set<string>;
  nextAnnotation?: Annotation;
}

export interface LoginInfo {
  isLoggedIn: boolean;
  error?: Error;
  magicLinkLogin: {
    companies?: AuthCompanyOptionDto[];
  };
}

export interface Languages {
  name: string;
  code: string;
}

export interface User {
  info?: Tenant;
  payment: PaymentInfo;
  loginInfo: LoginInfo;
  companyInfo?: CompanyInfo;
  leases?: Lease[];
  selectedLease?: Lease;
  maintenance: MaintenanceRequestInfo;
  chat: ChatSection;
  deactivatedLeases: Lease[];
  signerState: SignerState;
  languages?: Languages[];
  insurances?: {
    insurances: Insurance[];
    insurancePolicyFiles: {
      fileId: string;
      fileName: string;
      fileSize: number;
      signedThumbUrl: string;
      signedUrl: string;
    }[];
  };
  embeddedInsurance?: LeaseRentersInsuranceEmbedDto;
  moveOutRequests?: MoveOutRequest[];
}

export const DEFAULT_USER: User = {
  info: undefined,
  payment: {
    selectedTransaction: undefined,
    autoPaymentDetail: undefined,
    bankAccounts: undefined,
    plaid: undefined,
    selectedPaymentType: undefined,
    selectedPaymentAmount: undefined,
    selectedAutoPayment: {
      autoPayment: undefined,
      errors: new Map<PaymentErrorCode, ''>(),
    },
    paymentWithFees: undefined,
    autoPayments: undefined,
    stripe: {
      clientSecret: '',
      sessionId: '',
      isCheckoutFulfilled: false,
    },
  },
  loginInfo: {
    isLoggedIn: false,
    error: undefined,
    magicLinkLogin: {},
  },
  companyInfo: undefined,
  leases: undefined,
  selectedLease: undefined,
  maintenance: {
    maintenanceRequests: undefined,
    newRequest: undefined,
  },
  chat: {
    chats: undefined,
    selectedChat: undefined,
  },
  deactivatedLeases: [],
  signerState: {
    document: {
      id: '',
      leaseName: '',
      pages: [],
    },
    signedAnnotations: new Set<string>(),
  },
  languages: undefined,
  insurances: undefined,
  embeddedInsurance: undefined,
  moveOutRequests: [],
};

export class MainAppState extends AppState<User> implements CompanyIdProvider {
  user: User = structuredClone(DEFAULT_USER);

  public resetState(): void {
    this.user = structuredClone(DEFAULT_USER);
  }

  public getState(): User {
    return { ...this.user };
  }

  public getCompanyId = (): string | undefined => {
    return this.user.companyInfo?.id;
  };
}

export const mainAppState = new MainAppState();
